import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/kit/Button';
import { OtpInput } from '../../components/form/OtpInput';
import { useAuth } from '../../lib/auth';
import { setLanguage } from '../../lib/i18n';
import { setPreference } from '../../lib/preferences';
import { LANG_LIST, type LangCode } from '../../lib/i18n/languages';
import { haptic } from '../../lib/haptic';
import { BrandVideoBackdrop } from '../../components/auth/BrandVideoBackdrop';

export function OtpRoute() {
  const { t, i18n } = useTranslation('auth');
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const pending = useAuth((s) => s.pending);
  const verifyOtp = useAuth((s) => s.verifyOtp);
  const error = useAuth((s) => s.error);
  const clearError = useAuth((s) => s.clearError);
  const status = useAuth((s) => s.status);
  const nav = useNavigate();

  const activeLang = (i18n.language?.split('-')[0] as LangCode) || 'en';

  useEffect(() => {
    if (!pending) {
      // OTP session lost (e.g. page refresh). Restart.
      nav('/auth/phone', { replace: true });
    }
  }, [pending, nav]);

  useEffect(() => {
    if (status === 'authenticated') {
      nav('/', { replace: true });
    }
  }, [status, nav]);

  useEffect(() => {
    if (code.length === 6 && !submitting) {
      void onSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const onSubmit = async () => {
    if (code.length !== 6) return;
    haptic.success(); // 6-digit auto-submit fires — celebrate the moment
    clearError();
    setSubmitting(true);
    try {
      await verifyOtp(code);
    } catch {
      setSubmitting(false);
      setCode('');
      haptic.warn();
    }
  };

  const onPickLang = (lang: LangCode) => {
    if (lang === activeLang) return;
    haptic.tap();
    setLanguage(lang);                  // persists to LANG_STORAGE_KEY (fu_driver_lang)
    setPreference('language', lang);    // mirror into Task 1 preferences helper
  };

  if (!pending) return null;

  return (
    <>
      {/* Fullscreen brand video + gradient fallback (same component as
          PhoneRoute, so the cross-screen video stays smooth). */}
      <BrandVideoBackdrop />
      {/* Vignette / overlay so the glass card sits comfortably regardless
          of the frame showing behind it. */}
      <div
        aria-hidden
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 1,
          pointerEvents: 'none',
          background:
            'radial-gradient(ellipse 60% 50% at 50% 50%, rgba(255, 255, 255, 0.12), transparent 70%), linear-gradient(180deg, rgba(15, 59, 38, 0.10) 0%, rgba(15, 59, 38, 0.20) 100%)',
        }}
      />

      {/* Centered glass card */}
      <div
        style={{
          position: 'relative',
          zIndex: 2,
          minHeight: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          paddingBottom: 'calc(80px + env(safe-area-inset-bottom, 16px))',
        }}
      >
        <div
          /* Force light-mode tokens inside the glass card. See PhoneRoute
             for the rationale — same fix, same reason. */
          data-theme="light"
          style={{
            width: '100%',
            maxWidth: '400px',
            background: 'rgba(255, 255, 255, 0.78)',
            backdropFilter: 'blur(24px) saturate(180%)',
            WebkitBackdropFilter: 'blur(24px) saturate(180%)',
            border: '1px solid rgba(255, 255, 255, 0.55)',
            borderRadius: '20px',
            boxShadow: '0 24px 60px rgba(15, 59, 38, 0.14), 0 4px 12px rgba(15, 59, 38, 0.06)',
            padding: '32px 28px',
          }}
        >
          <h1
            style={{
              fontSize: '28px',
              fontWeight: 800,
              lineHeight: 1.15,
              letterSpacing: '-0.025em',
              textAlign: 'center',
              color: 'var(--color-text)',
              margin: '0 0 6px',
            }}
          >
            {/* Cross-language editorial italic: `OTP` stays in Latin script
                across all 8 languages so the Fraunces italic flourish reads
                identically everywhere. Only the action verb translates.
                See feedback_cross_language_editorial_italic.md */}
            <span className="serif-accent">{t('otpAnchor')}</span>{' '}
            {t('otpActionVerb')}
          </h1>
          <p
            style={{
              fontSize: '15px',
              lineHeight: 1.5,
              textAlign: 'center',
              color: 'var(--color-text-secondary)',
              margin: '0 0 24px',
            }}
          >
            {t('sentTo', { phone: pending.phone })}
          </p>

          <OtpInput value={code} onChange={setCode} error={!!error} />
          {error ? (
            <p
              style={{
                fontSize: '14px',
                color: 'var(--color-danger)',
                marginTop: '12px',
              }}
            >
              {error}
            </p>
          ) : null}
          <div
            style={{
              marginTop: '16px',
              fontSize: '13px',
              color: 'var(--color-text-secondary)',
              textAlign: 'center',
            }}
          >
            {submitting ? t('verifying') : t('autoSubmit')}
          </div>
          <div style={{ marginTop: '20px' }}>
            {/* Block the "different number" tap during verify so a slow-network
                flap doesn't strand the driver on the Phone screen while the
                in-flight verifyOtp resolves successfully behind them. */}
            <Button variant="ghost" disabled={submitting} onClick={() => { haptic.tap(); nav('/auth/phone'); }}>
              {t('useDifferentNumber')}
            </Button>
          </div>
        </div>

        {/* Language picker pill row — fixed at the bottom, scrolls horizontally
            on overflow. Glass pills so the row blends with the auth aesthetic.
            Mirrors PhoneRoute exactly so language picks persist seamlessly
            across the phone → OTP transition. data-theme="light" forces dark
            pill text regardless of device theme (pills are white-translucent
            in both themes). */}
        <div
          role="group"
          aria-label="Language"
          data-theme="light"
          style={{
            position: 'fixed',
            bottom: 'env(safe-area-inset-bottom, 16px)',
            // Pin to the same 440px column the rest of the app uses.
            // On mobile this collapses to full-width; on desktop the
            // pills no longer hug the bottom-left of the viewport.
            left: '50%',
            transform: 'translateX(-50%)',
            width: '100%',
            maxWidth: 440,
            display: 'flex',
            gap: '8px',
            overflowX: 'auto',
            padding: '12px 16px',
            zIndex: 3,
            scrollbarWidth: 'none',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {LANG_LIST.map((lang) => {
            const active = activeLang === lang.code;
            return (
              <button
                key={lang.code}
                type="button"
                onClick={() => onPickLang(lang.code)}
                aria-pressed={active}
                style={{
                  flexShrink: 0,
                  padding: '8px 14px',
                  borderRadius: 'var(--radius-pill)',
                  background: active ? 'rgba(255, 255, 255, 0.92)' : 'rgba(255, 255, 255, 0.55)',
                  backdropFilter: 'blur(12px) saturate(180%)',
                  WebkitBackdropFilter: 'blur(12px) saturate(180%)',
                  border: active ? '1px solid rgba(46, 158, 101, 0.45)' : '1px solid rgba(255, 255, 255, 0.55)',
                  boxShadow: active ? '0 4px 14px rgba(15, 59, 38, 0.14)' : '0 2px 8px rgba(15, 59, 38, 0.08)',
                  fontSize: '14px',
                  fontWeight: active ? 700 : 500,
                  color: active ? 'var(--green-700)' : 'var(--color-text)',
                  whiteSpace: 'nowrap',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                {lang.native}
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}
